import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, Sparkles, Check } from 'lucide-react';
import { api, type SkillSummary, type SkillCategory } from '../lib/api';
import { SkillIcon } from '../components/SkillIcon';
import { relativeDate } from '../lib/date';

const TOOL_LABELS: Record<string, string> = {
  claude_code: 'Claude Code',
  chatgpt: 'ChatGPT',
  codex: 'Codex',
  cursor: 'Cursor',
  gemini: 'Gemini',
};

const ACCENTS: Record<string, { chip: string; ring: string; soft: string }> = {
  indigo: { chip: 'bg-indigo-50 text-indigo-700', ring: 'ring-indigo-200', soft: 'bg-indigo-50' },
  emerald: { chip: 'bg-emerald-50 text-emerald-700', ring: 'ring-emerald-200', soft: 'bg-emerald-50' },
  amber: { chip: 'bg-amber-50 text-amber-700', ring: 'ring-amber-200', soft: 'bg-amber-50' },
  sky: { chip: 'bg-sky-50 text-sky-700', ring: 'ring-sky-200', soft: 'bg-sky-50' },
};
const accent = (a?: string) => ACCENTS[a || 'indigo'] || ACCENTS.indigo;

const font = { fontFamily: "'Outfit', sans-serif" };

export function MarketplacePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [skills, setSkills] = useState<SkillSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  const activeCategory = searchParams.get('category') || '';

  useEffect(() => {
    api.listSkillCategories().then((res) => {
      const data = (res.data as any)?.data || res.data;
      if (Array.isArray(data)) setCategories(data);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    api.listSkills({ category: activeCategory || undefined }).then((res) => {
      const data = (res.data as any)?.data || res.data;
      if (Array.isArray(data)) setSkills(data);
      setLoading(false);
    });
  }, [activeCategory]);

  const filtered = useMemo(() => {
    if (!q.trim()) return skills;
    const needle = q.toLowerCase();
    return skills.filter((s) =>
      [s.name, s.tagline, s.description].filter(Boolean).some((t) => (t as string).toLowerCase().includes(needle)),
    );
  }, [skills, q]);

  const featured = useMemo(() => filtered.find((s) => s.featured), [filtered]);
  const rest = useMemo(() => filtered.filter((s) => s.id !== featured?.id), [filtered, featured]);

  const setCategory = (slug: string) => {
    const next = new URLSearchParams(searchParams);
    if (slug) next.set('category', slug);
    else next.delete('category');
    setSearchParams(next);
  };

  return (
    <div className="mx-auto max-w-6xl px-6 py-8">
      {/* Hero */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 rounded-full bg-mazo-50 px-3 py-1 text-xs font-medium text-mazo-700">
          <Sparkles className="h-3.5 w-3.5" /> GTM skills for B2B SaaS founders
        </div>
        <h1 className="mt-3 text-3xl font-semibold text-gray-900" style={font}>
          The GTM brain for founders going 0 → €1M
        </h1>
        <p className="mt-2 max-w-2xl text-gray-600" style={font}>
          Battle-tested go-to-market skills — positioning, outbound, founder content, lifecycle —
          for founders still doing GTM themselves. Run them in Claude, ChatGPT or Cursor; we keep
          them sharp and ship new ones as you grow.
        </p>
      </div>

      {/* Search */}
      <div className="mb-5 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search skills…"
            className="w-full rounded-xl border border-gray-200 bg-white py-2.5 pl-10 pr-4 text-sm text-gray-900 outline-none focus:border-mazo-400 focus:ring-2 focus:ring-mazo-100"
            style={font}
          />
        </div>
      </div>

      {/* Category chips */}
      <div className="mb-8 flex flex-wrap gap-2">
        <button
          onClick={() => setCategory('')}
          className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${!activeCategory ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'}`}
          style={font}
        >
          All skills
        </button>
        {categories.map((c) => (
          <button
            key={c.id}
            onClick={() => setCategory(c.slug)}
            className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${activeCategory === c.slug ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50'}`}
            style={font}
          >
            {c.name}
            {typeof c.skill_count === 'number' && c.skill_count > 0 && <span className="ml-1.5 opacity-60">{c.skill_count}</span>}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 text-center text-gray-400" style={font}>Loading skills…</div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center text-gray-400" style={font}>No skills match your search.</div>
      ) : (
        <>
          {/* Featured */}
          {featured && (
            <button
              onClick={() => navigate(`/skills/${featured.slug}`)}
              className={`mb-8 block w-full rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-6 text-left ring-1 ${accent(featured.category?.accent).ring} transition-shadow hover:shadow-md`}
            >
              <div className="flex items-start gap-5">
                <SkillIcon icon={featured.icon} emoji={featured.emoji} accent={featured.category?.accent} size={64} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full bg-mazo-600 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">Featured</span>
                    {featured.category && (
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${accent(featured.category.accent).chip}`}>{featured.category.name}</span>
                    )}
                  </div>
                  <h2 className="mt-2 text-xl font-semibold text-gray-900" style={font}>{featured.name}</h2>
                  <p className="mt-1 text-gray-600" style={font}>{featured.tagline}</p>
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    {(featured.compatible_tools || []).map((t) => (
                      <span key={t} className="rounded-md bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{TOOL_LABELS[t] || t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0 self-center">
                  {featured.owned ? (
                    <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700"><Check className="h-4 w-4" /> Owned</span>
                  ) : (
                    <span className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white" style={font}>View skill</span>
                  )}
                </div>
              </div>
            </button>
          )}

          {/* Grid */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {rest.map((s) => (
              <button
                key={s.id}
                onClick={() => navigate(`/skills/${s.slug}`)}
                className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-5 text-left transition-shadow hover:shadow-md"
              >
                <div className="mb-3 flex items-center justify-between">
                  <SkillIcon icon={s.icon} emoji={s.emoji} accent={s.category?.accent} size={44} />
                  {s.owned ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700"><Check className="h-3 w-3" /> Owned</span>
                  ) : s.category ? (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${accent(s.category.accent).chip}`}>{s.category.name}</span>
                  ) : null}
                </div>
                <h3 className="text-base font-semibold text-gray-900" style={font}>{s.name}</h3>
                <p className="mt-1 flex-1 text-sm text-gray-600" style={font}>{s.tagline}</p>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {(s.compatible_tools || []).slice(0, 4).map((t) => (
                    <span key={t} className="rounded-md bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-500">{TOOL_LABELS[t] || t}</span>
                  ))}
                </div>
                {s.updated_at && (
                  <div className="mt-2.5 text-[11px] text-gray-400" style={font}>Updated {relativeDate(s.updated_at)}</div>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default MarketplacePage;
